import { motion } from 'framer-motion';
import moment from 'moment';
import { OutlineChevronRightIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Typography } from '@tempmee/react';
import { Checkbox, Column, Pressable } from '~/commonComponents';
import { Date } from '~/components/Schedule/components/JobCard/components/Date';
import { IEligibleJob } from '~/domain/EligibleJobs';

interface IEligibleJobCardProps {
  job: IEligibleJob;
  isSelected: boolean;
  onSelect: (jobId: string) => void;
  disabled?: boolean;
  onCardPress: (jobId: string) => void;
}

const getJobStatusColor = (status: string): string => {
  switch (status) {
    case 'open':
      return colors.success[500];
    case 'pending-counter':
      return colors.error[450];
    default:
      return colors.neutral[400];
  }
};

const getJobStatusLabel = (status: string): string => {
  switch (status) {
    case 'open':
      return 'OPEN';
    case 'pending-counter':
      return 'ACTION NEEDED';
    default:
      return status;
  }
};

export const EligibleJobCard = ({
  job,
  isSelected,
  onSelect,
  disabled,
  onCardPress,
}: IEligibleJobCardProps) => {
  const statusColor = getJobStatusColor(job.status);
  const statusLabel = getJobStatusLabel(job.status);

  const handleButtonPress = () => {
    onCardPress(job.id);
  };

  return (
    <motion.button
      type="button"
      onClick={handleButtonPress}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: colors.shades[0],
        borderRadius: 8,
        height: 72,
        padding: '12px',
        width: 400,
        boxShadow: 'rgba(0, 0, 0, 0.15) 0px 1px 3px 1px',
        transition: 'all 0.2s ease',
      }}
      whileHover={{
        boxShadow: '0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.15)',
        transition: { duration: 0.3 },
      }}
    >
      <Pressable
        onPress={(event) => {
          event.stopPropagation();
          onSelect(job.id);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          marginRight: 8,
          padding: 8,
        }}
        disabled={disabled}
      >
        <Checkbox checked={isSelected} />
      </Pressable>

      <div style={{ width: 60, marginRight: 16 }}>
        <Date
          backgroundColor={statusColor}
          localDate={moment(job.localDate, 'ddd, MMM D, YYYY').format('YYYY-MM-DD')}
        />
      </div>

      <Column flex={1}>
        <Typography as="span" bold>{job.professionName}</Typography>
        <Typography as="span">
          {job.localStartTime} - {job.localEndTime}
        </Typography>
        <Typography as="span" color={statusColor}>{statusLabel}</Typography>
      </Column>

      <OutlineChevronRightIcon size={24} color={colors.secondary[500]} />
    </motion.button>
  );
};
